import _ from 'lodash';
import type {
  AppData,
  CountryData,
  PerimeterConfig,
  Site,
  Suspect,
  ThreatProfile,
} from '../../shared/types/index.js';
import type { Position } from '../api/types.js';
import { getFlagEmoji } from '../utils/Helpers.js';
import { haversine } from './geo.js';

/**
 * Pure threat assessment: given the latest suspects from the API and the previous positions,
 * classifies every suspect against its site's perimeters, matches threat profiles, builds trails
 * and scores, recomputes site status, and reports what changed as events. No side effects and no
 * settings: what to show for an event (toast, overlay, auto-pan) is the caller's policy.
 */

export interface ThreatInput {
  /** Fresh suspects from the API, keyed by id, with app names already attached. */
  suspects: Record<string, Suspect>;
  prevPositions: Readonly<Record<string, Position>>;
  sites: ReadonlyArray<Site>;
  perimeters: ReadonlyArray<PerimeterConfig>;
  profiles: ReadonlyArray<ThreatProfile>;
  /** Suspects the analyst marked as not hostile; dropped from the result. */
  notHostileIds: ReadonlyArray<string>;
  breachFired: Readonly<Record<string, boolean>>;
}

export type ThreatEvent =
  | {
      /** A suspect crossed inward into a closer perimeter (or was seen inside one for the first time). */
      kind: 'perimeter-entry';
      suspect: Suspect;
      /** 0 = innermost (critical) … 3 = outermost (watch). */
      perimeterIdx: number;
      perimeters: PerimeterConfig['perimeters'];
      site: Site;
    }
  | {
      /** A suspect entered the innermost perimeter. */
      kind: 'breach';
      suspect: Suspect;
      site: Site;
      dist: number;
    };

export interface ThreatState {
  /** Annotated suspects (level, hostile flags, matched profiles, origin, reasons); not-hostile ids removed. */
  suspects: Record<string, Suspect>;
  /** Only suspects that fall inside a perimeter of a known site have a position. */
  positions: Record<string, Position>;
  sites: Array<Site>;
  profiles: Array<ThreatProfile>;
  events: Array<ThreatEvent>;
  breachFired: Record<string, boolean>;
}

const TRAIL_LENGTH = 6;
const MIN_SCORE = 8;
const PROXIMITY_STATUS: Array<Position['status']> = ['breach', 'inzone', 'approach', 'flagged'];
const PROXIMITY_LEVEL: Array<Suspect['level']> = ['h', 'h', 'm', 'l'];

const REASON_APPS = 'Suspicious apps detected';
const REASON_COUNTRIES = 'Visited countries on a risk list';
const REASON_LANGUAGE = 'Suspicious language detected';

interface ProfileMatch {
  hostileLang: boolean;
  hostileApps: Array<AppData>;
  hostileCountries: Array<CountryData>;
  profiles: Array<string>;
}

/** Matches one suspect against the threat profiles attached to its perimeter set; bumps each matched profile's count. */
function matchProfiles(
  suspect: Suspect,
  profileIds: ReadonlyArray<string>,
  profiles: Map<string, ThreatProfile>,
  reasons: Set<string>
): ProfileMatch {
  let hostileLang = false;
  const hostileApps = new Set<AppData>();
  const hostileCountries = new Set<CountryData>();
  const matched = new Set<string>();

  profileIds.forEach(id => {
    const profile = profiles.get(id);
    if (!profile) return;

    const isLangHostile =
      profile.hostileProps.languages.length > 0 &&
      profile.hostileProps.languages.includes(suspect.lang.toLowerCase());
    if (isLangHostile) hostileLang = true;

    const isCountryHostile =
      profile.hostileProps.countries.length > 0 &&
      suspect.countries.filter(country => {
        const isHostile = profile.hostileProps.countries.includes(country.cc2);
        if (isHostile) hostileCountries.add(country);
        return isHostile;
      }).length > 0;

    if (isLangHostile) reasons.add(REASON_LANGUAGE);
    if (isCountryHostile) reasons.add(REASON_COUNTRIES);

    let result = isLangHostile || isCountryHostile;

    if (profile.hostileProps.appBundles.length) {
      // Profile keys are `${bundle}__${os}`; suspect app keys are bare bundles.
      const hostileAppBundles = profile.hostileProps.appBundles.map(({ bundle }) => bundle.split('__')[0]);
      suspect.apps.forEach(app => {
        if (hostileAppBundles.includes(app.bundle)) {
          result = true;
          hostileApps.add(app);
        }
      });
    }

    if (result) {
      profile.count++;
      matched.add(profile.name);
    }
  });

  return {
    hostileLang,
    hostileApps: Array.from(hostileApps),
    hostileCountries: Array.from(hostileCountries),
    profiles: Array.from(matched),
  };
}

function newPosition(baseScore: number): Position {
  return { lat: 0, lng: 0, trail: [], score: baseScore, nearDist: 9999, status: 'flagged' };
}

export function computeThreatState(input: ThreatInput): ThreatState {
  const suspects: Record<string, Suspect> = {};
  const positions: Record<string, Position> = {};
  const sites = _.cloneDeep(input.sites) as Array<Site>;
  const profiles = new Map<string, ThreatProfile>();
  const events: Array<ThreatEvent> = [];
  const breachFired: Record<string, boolean> = { ...input.breachFired };

  _.cloneDeep(input.profiles).forEach(profile => {
    profile.count = 0;
    profiles.set(profile.id, profile);
  });

  Object.values(input.suspects).forEach(raw => {
    if (input.notHostileIds.includes(raw.id)) return;

    const suspect: Suspect = { ...raw };

    // A candidate of a deleted (or unknown) site cannot be placed on the map; it is dropped everywhere,
    // otherwise the watchlist would keep listing it without a marker.
    const site = sites.find(candidate => candidate.id === suspect.siteId);
    if (!site) return;
    const perimeter = input.perimeters.find(config => config.id === site.perimeterId);
    if (!perimeter) return;
    suspects[suspect.id] = suspect;

    const distance = haversine(suspect.lat, suspect.lng, site.lat, site.lng);
    const perimeterIdx = perimeter.perimeters.findIndex(p => p.upperBound > distance);
    if (perimeterIdx === -1) return;

    // Threat profile matching
    const reasons = new Set(suspect.reasons);
    const match = matchProfiles(suspect, perimeter.threatProfilesIds, profiles, reasons);
    suspect.hostileLang = match.hostileLang;
    suspect.hostileApps = match.hostileApps;
    suspect.hostileCountries = match.hostileCountries;
    suspect.profiles = match.profiles;
    if (suspect.countries.length) {
      suspect.origin = `${suspect.countries[0].name} ${getFlagEmoji(suspect.countries[0].cc2)}`;
    }
    if (suspect.hostileApps.length) reasons.add(REASON_APPS);
    if (suspect.hostileCountries.length) reasons.add(REASON_COUNTRIES);
    suspect.reasons = Array.from(reasons);

    // Position, trail and score
    const prev = input.prevPositions[suspect.id];
    const pos: Position = prev ? _.cloneDeep(prev) : newPosition(suspect.baseScore);
    positions[suspect.id] = pos;

    let isNewCoords = false;
    if (!pos.trail.length || pos.lat !== suspect.lat || pos.lng !== suspect.lng) {
      pos.trail = !pos.trail.length
        ? suspect.waypoints.slice(0, TRAIL_LENGTH)
        : [...(pos.trail.length > TRAIL_LENGTH ? pos.trail.slice(1) : pos.trail), [suspect.lat, suspect.lng]];
      isNewCoords = true;
    }
    pos.lat = suspect.lat;
    pos.lng = suspect.lng;
    pos.nearDist = distance;
    pos.nearLoc = site;
    const oldPerimeterIdx = pos.perimeterIdx;
    pos.perimeterIdx = perimeterIdx;
    const outerBound = perimeter.perimeters[perimeter.perimeters.length - 1].upperBound;
    pos.score = Math.max(MIN_SCORE, Math.round(((outerBound - distance) / outerBound) * 100));

    suspect.level = PROXIMITY_LEVEL[perimeterIdx] ?? 'l';
    pos.status = PROXIMITY_STATUS[perimeterIdx] ?? 'flagged';

    // Events fire only on inward crossings (or the first sighting inside a perimeter)
    const crossedInward = isNewCoords && (oldPerimeterIdx === undefined || oldPerimeterIdx > perimeterIdx);
    if (perimeterIdx === 0 && crossedInward && !breachFired[suspect.id]) {
      events.push({ kind: 'breach', suspect: { ...suspect }, site, dist: distance });
    }
    if (crossedInward) {
      events.push({
        kind: 'perimeter-entry',
        suspect: { ...suspect, score: 0 },
        perimeterIdx,
        perimeters: perimeter.perimeters,
        site,
      });
    }
    // NOTE: breachFired is only ever reset here, never set — kept as in the original implementation,
    // so a suspect re-entering the critical perimeter after leaving it triggers the overlay again.
    if (perimeterIdx > 1) breachFired[suspect.id] = false;
  });

  // Site status follows the closest suspect
  const nearestIdxBySite = new Map<string, number>();
  Object.values(positions).forEach(pos => {
    if (!pos.nearLoc || pos.perimeterIdx === undefined) return;
    const current = nearestIdxBySite.get(pos.nearLoc.id);
    if (current === undefined || current > pos.perimeterIdx) {
      nearestIdxBySite.set(pos.nearLoc.id, pos.perimeterIdx);
    }
  });
  sites.forEach(site => {
    const nearest = nearestIdxBySite.get(site.id);
    site.status = nearest === undefined ? 'safe' : nearest <= 1 ? 'alert' : 'watch';
  });

  return { suspects, positions, sites, profiles: Array.from(profiles.values()), events, breachFired };
}
